import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Upload, FileText, Image as ImageIcon, X, Loader2, Sparkles, Merge, CheckCircle2, AlertCircle, Clock, Eye, ExternalLink } from "lucide-react";
import { ImportReview } from "@/components/ImportReview";
import ImportEmailPanel from "@/components/ImportEmailPanel";

const ACCEPT = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
let lotSeq = 0;
const GROUP_WINDOW_MS = 3 * 60 * 1000;

const seqNumber = (name) => { const m = name.match(/(\d+)(?=\.[a-z0-9]+$)/i); return m ? parseInt(m[1], 10) : null; };

// Photos prises à la suite (même minute environ ou numéros de fichier consécutifs) = pages d'un même constat
function groupConsecutive(files) {
  const groups = [];
  let cur = null;
  files.forEach((f) => {
    const isImg = f.type !== "application/pdf";
    const prev = cur && cur[cur.length - 1];
    const close = prev && isImg && prev.type !== "application/pdf" && (
      Math.abs(f.lastModified - prev.lastModified) <= GROUP_WINDOW_MS
      || (seqNumber(f.name) !== null && seqNumber(prev.name) !== null && Math.abs(seqNumber(f.name) - seqNumber(prev.name)) === 1)
    );
    if (close && cur.length < 6) cur.push(f);
    else { cur = [f]; groups.push(cur); }
  });
  return groups;
}

export default function ConstatImport() {
  const navigate = useNavigate();
  const [lots, setLots] = useState([]);
  const [selected, setSelected] = useState([]);
  const [running, setRunning] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [typologies, setTypologies] = useState([]);
  const [autoGroup, setAutoGroup] = useState(true);
  const inputRef = useRef();
  const lotsRef = useRef(lots);
  lotsRef.current = lots;

  useEffect(() => { api.get("/typologies").then((r) => setTypologies(r.data)); }, []);

  useEffect(() => {
    const lotId = new URLSearchParams(window.location.search).get("lot");
    if (!lotId) return;
    api.get(`/import/lots/${lotId}`).then(({ data }) => {
      setLots(data.items.map((it) => ({
        id: ++lotSeq, files: it.fichiers.map((f) => ({ name: f.name, type: f.content_type })),
        status: it.etat_id ? "cree" : it.statut === "erreur" ? "erreur" : it.statut === "en_cours" ? "en_cours" : "a_verifier",
        result: it.proposition ? { import_id: it.import_id, fichiers: it.fichiers, proposition: it.proposition } : null,
        etatId: it.etat_id, error: it.erreur,
      })));
    }).catch(() => toast.error("Lot d'import introuvable"));
  }, []);

  const addFiles = (list) => {
    const ok = Array.from(list).filter((f) => ACCEPT.includes(f.type));
    if (ok.length !== list.length) toast.error("Seuls les PDF et images (JPEG, PNG, WebP) sont acceptés.");
    const groups = autoGroup ? groupConsecutive(ok) : ok.map((f) => [f]);
    const merged = groups.filter((g) => g.length > 1).length;
    if (merged) toast.message(`${merged} groupe${merged > 1 ? "s" : ""} de photos consécutives regroupé${merged > 1 ? "s" : ""} (recto/verso). Décochez l'option pour les séparer.`);
    setLots((p) => [...p, ...groups.map((g) => ({ id: ++lotSeq, files: g, status: "attente" }))]);
  };
  const patch = (id, changes) => setLots((p) => p.map((l) => (l.id === id ? { ...l, ...changes } : l)));
  const removeLot = (id) => { setLots((p) => p.filter((l) => l.id !== id)); setSelected((s) => s.filter((x) => x !== id)); };
  const merge = () => {
    const chosen = lots.filter((l) => selected.includes(l.id) && l.status === "attente");
    if (chosen.length < 2) return;
    const merged = { id: ++lotSeq, files: chosen.flatMap((l) => l.files), status: "attente" };
    setLots((p) => [...p.filter((l) => !selected.includes(l.id)), merged]);
    setSelected([]);
  };

  const analyseAll = async () => {
    setRunning(true);
    const pendingLots = lotsRef.current.filter((l) => l.status === "attente");
    const jobs = [];
    for (const lot of pendingLots) {
      patch(lot.id, { status: "en_cours" });
      const fd = new FormData();
      lot.files.forEach((f) => fd.append("files", f));
      try {
        const { data: job } = await api.post("/import/analyser", fd, { timeout: 120000 });
        jobs.push({ lot, job });
      } catch (e) {
        patch(lot.id, { status: "erreur", error: apiError(e) });
      }
    }
    if (jobs.length) {
      try {
        const { data } = await api.post("/import/lots", { import_ids: jobs.map((j) => j.job.import_id) });
        window.history.replaceState(null, "", `/constats/importer?lot=${data.id}`);
        if (jobs.length > 1) toast.message("Vous recevrez un e-mail avec le lien de relecture dès que tout le lot sera analysé.");
      } catch (e) { console.warn("Création du lot d'import impossible (notification facultative)", e?.message); }
    }
    await Promise.all(jobs.map(async ({ lot, job }) => {
      try {
        let data = job;
        const started = Date.now();
        while (data.statut === "en_cours") {
          if (Date.now() - started > 15 * 60 * 1000) throw new Error("Analyse trop longue — réessayez avec un fichier plus léger");
          await new Promise((r) => setTimeout(r, 3000));
          data = (await api.get(`/import/${job.import_id}`)).data;
        }
        if (data.statut === "erreur") throw new Error(data.erreur || "Analyse impossible");
        patch(lot.id, { status: "a_verifier", result: data });
      } catch (e) {
        patch(lot.id, { status: "erreur", error: e?.response ? apiError(e) : (e.message || "Erreur") });
      }
    }));
    setRunning(false);
    toast.success("Analyse du lot terminée");
  };

  const creer = async (lot, proposition) => {
    const { data } = await api.post("/import/creer", { import_id: lot.result.import_id, proposition });
    patch(lot.id, { status: "cree", etatId: data.id });
    setReviewId(null);
    toast.success("Constat importé en brouillon");
  };

  const reviewLot = lots.find((l) => l.id === reviewId);
  const done = lots.filter((l) => ["a_verifier", "cree", "erreur"].includes(l.status)).length;
  const pending = lots.filter((l) => l.status === "attente").length;

  if (reviewLot) {
    return (
      <div className="max-w-5xl">
        <button onClick={() => setReviewId(null)} className="flex items-center text-sm text-dd-blue hover:underline mb-4" data-testid="import-review-back">
          <ArrowLeft size={16} className="mr-1" /> Retour à la file d'import
        </button>
        <h1 className="text-3xl font-bold text-dd-blue mb-6">Vérifier : {reviewLot.files.map((f) => f.name).join(" + ")}</h1>
        <ImportReview initial={reviewLot.result.proposition} fichiers={reviewLot.result.fichiers} typologies={typologies}
          onCreate={(p) => creer(reviewLot, p)} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate("/constats")} className="flex items-center text-sm text-dd-blue hover:underline mb-4" data-testid="import-back">
        <ArrowLeft size={16} className="mr-1" /> Retour aux constats
      </button>
      <h1 className="text-3xl font-bold text-dd-blue">Importer d'anciens constats</h1>
      <p className="text-sm text-dd-warm mt-1 mb-6">
        Déposez un ou des dizaines de fichiers (PDF, scans, photos, même manuscrits). Par défaut, un fichier = un constat ; sélectionnez plusieurs fichiers puis « Fusionner » pour les regrouper (ex. PDF + photos). Claude analyse chaque constat, vous vérifiez, puis il est recréé en brouillon.
      </p>

      <ImportEmailPanel />

      <div className="bg-white rounded-xl border border-dd-light p-6 space-y-4">
        <div data-testid="import-dropzone" onClick={() => inputRef.current.click()}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          className="border-2 border-dashed border-dd-light rounded-xl p-8 text-center cursor-pointer hover:border-dd-blue hover:bg-dd-light/30 transition-colors">
          <Upload className="mx-auto text-dd-blue mb-3" size={32} />
          <p className="font-medium text-dd-night">Glissez vos fichiers ici ou touchez pour choisir</p>
          <p className="text-xs text-dd-warm mt-1">PDF, JPEG, PNG, WebP · 20 Mo max par fichier · les photos sont redressées et contrastées automatiquement</p>
          <input ref={inputRef} type="file" multiple accept={ACCEPT.join(",")} className="hidden" data-testid="import-file-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>
        <label className="flex items-center gap-2 text-sm text-dd-night" onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={autoGroup} onCheckedChange={(v) => setAutoGroup(!!v)} data-testid="import-autogroup" />
          Regrouper automatiquement les photos prises à la suite (recto/verso, pages multiples) dans un même constat
        </label>

        {lots.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-dd-night flex-1" data-testid="import-progress-text">
                {lots.length} constat{lots.length > 1 ? "s" : ""} · {done} traité{done > 1 ? "s" : ""} · {pending} en attente
              </p>
              {selected.length >= 2 && (
                <Button variant="outline" size="sm" onClick={merge} className="h-9" data-testid="import-merge-btn"><Merge size={14} className="mr-1" /> Fusionner ({selected.length})</Button>
              )}
              <Button data-testid="import-analyse-btn" disabled={!pending || running} onClick={analyseAll} className="h-11 bg-dd-blue hover:bg-dd-night text-white">
                {running ? <Loader2 className="animate-spin mr-2" size={18} /> : <Sparkles className="mr-2" size={18} />}
                {running ? "Analyse en cours…" : `Analyser ${pending > 1 ? `les ${pending} constats` : "avec Claude"}`}
              </Button>
            </div>
            {(running || done > 0) && (
              <div className="h-2 rounded-full bg-dd-light overflow-hidden" data-testid="import-progress-bar">
                <div className="h-full bg-dd-blue transition-[width] duration-500" style={{ width: `${(done / lots.length) * 100}%` }} />
              </div>
            )}
            <ul className="divide-y divide-dd-light" data-testid="import-file-list">
              {lots.map((lot, i) => (
                <LotRow key={lot.id} lot={lot} index={i} selectable={lot.status === "attente" && !running}
                  checked={selected.includes(lot.id)} onCheck={(v) => setSelected((s) => (v ? [...s, lot.id] : s.filter((x) => x !== lot.id)))}
                  onRemove={() => removeLot(lot.id)} onReview={() => setReviewId(lot.id)} onOpen={() => navigate(`/constats/${lot.etatId}`)} />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

function LotRow({ lot, index, selectable, checked, onCheck, onRemove, onReview, onOpen }) {
  const icon = {
    attente: <Clock size={16} className="text-dd-warm" />, en_cours: <Loader2 size={16} className="animate-spin text-dd-blue" />,
    a_verifier: <Eye size={16} className="text-amber-600" />, cree: <CheckCircle2 size={16} className="text-green-600" />,
    erreur: <AlertCircle size={16} className="text-destructive" />,
  }[lot.status];
  const label = { attente: "En attente", en_cours: "Analyse…", a_verifier: "À vérifier", cree: "Créé", erreur: "Erreur" }[lot.status];
  return (
    <li className="flex items-center gap-3 py-2.5 text-sm" data-testid={`import-lot-${index}`}>
      {selectable ? <Checkbox checked={checked} onCheckedChange={onCheck} data-testid={`import-lot-check-${index}`} /> : <span className="w-4" />}
      <span className="shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1.5">
          {lot.files.map((f, j) => (
            <span key={j} className="inline-flex items-center gap-1 truncate text-dd-night bg-dd-light/60 rounded px-1.5 py-0.5 text-xs">
              {f.type === "application/pdf" ? <FileText size={12} /> : <ImageIcon size={12} />}{f.name}
            </span>
          ))}
        </div>
        {lot.status === "a_verifier" && lot.result && <p className="text-xs text-dd-warm mt-0.5">{lot.result.proposition.entete?.immeuble || "—"} · {lot.result.proposition.entete?.locataire_sortant || lot.result.proposition.entete?.locataire_entrant || "—"} · confiance {Math.round((lot.result.proposition.confiance || 0) * 100)}%</p>}
        {lot.status === "erreur" && <p className="text-xs text-destructive mt-0.5">{lot.error}</p>}
      </div>
      <Badge className="bg-dd-light text-dd-night border-0 shrink-0" data-testid={`import-lot-status-${index}`}>{label}</Badge>
      {lot.status === "a_verifier" && <Button size="sm" onClick={onReview} className="h-9 bg-dd-blue text-white" data-testid={`import-lot-review-${index}`}>Vérifier</Button>}
      {lot.status === "cree" && <Button size="sm" variant="outline" onClick={onOpen} className="h-9" data-testid={`import-lot-open-${index}`}><ExternalLink size={14} className="mr-1" /> Ouvrir</Button>}
      {(lot.status === "attente" || lot.status === "erreur") && (
        <button onClick={onRemove} className="text-dd-warm hover:text-destructive" data-testid={`import-remove-${index}`}><X size={16} /></button>
      )}
    </li>
  );
}
